import React, { useCallback, useEffect, useState } from "react";
import { ChartBar, DeviceMobile, Desktop, Globe, ArrowClockwise, MapPin } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

function BreakdownRows({ title, icon: Icon, rows, total, max = 6 }) {
  return (
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-2">
        <Icon size={12} weight="bold" /> {title}
      </div>
      <div className="space-y-1.5">
        {rows.slice(0, max).map((r) => {
          const pct = total ? Math.round((r.count / total) * 100) : 0;
          return (
            <div key={r.name} data-testid={`analytics-${title.toLowerCase()}-${r.name}`}>
              <div className="flex items-center justify-between text-[11px] font-semibold text-tac-text">
                <span>{r.name}</span>
                <span className="font-mono-tac text-tac-dim">{r.count} · {pct}%</span>
              </div>
              <div className="h-1.5 bg-tac-surface2 rounded-sm overflow-hidden">
                <div className="h-full bg-tac-primary/80" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="text-[11px] text-tac-faint">No data yet</div>}
      </div>
    </div>
  );
}

export const AnalyticsCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    api.get("/metrics/analytics", { params: { days: 30 } })
      .then(({ data }) => setData(data))
      .catch(() => setErr("Could not load analytics."));
  }, []);

  useEffect(() => {
    // Wait for the auth session to resolve — firing before the token
    // refresh completes 401s and used to hide the card silently.
    if (user && user !== false && user.role === "admin") load();
  }, [user, load]);

  if (err) {
    return (
      <div className="bg-tac-surface/40 border border-tac-border rounded-sm p-5" data-testid="admin-analytics-card">
        <div className="flex items-center gap-2 mb-2">
          <ChartBar size={16} weight="fill" className="text-tac-primary" />
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-tac-text">
            Visitor Analytics · last 30 days
          </h2>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-tac-dim">
          {err}
          <button
            data-testid="analytics-retry-btn"
            onClick={load}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-tac-border rounded-sm hover:border-tac-borderBright text-tac-text"
          >
            <ArrowClockwise size={11} weight="bold" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const maxDay = data ? Math.max(1, ...data.daily.map((d) => d.visitors)) : 1;

  return (
    <div className="bg-tac-surface/40 border border-tac-border rounded-sm p-5 space-y-5" data-testid="admin-analytics-card">
      <div className="flex items-center gap-2">
        <ChartBar size={16} weight="fill" className="text-tac-primary" />
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-tac-text">
          Visitor Analytics · last 30 days
        </h2>
      </div>

      {!data ? (
        <div className="text-[11px] text-tac-faint">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 divide-x divide-tac-border/60 border border-tac-border/60 rounded-sm">
            {[
              ["Today", data.unique_today, "analytics-unique-today"],
              ["Last 7 days", data.unique_7d, "analytics-unique-7d"],
              ["Last 30 days", data.unique_total, "analytics-unique-30d"],
            ].map(([label, val, tid]) => (
              <div key={label} className="px-4 py-2.5" data-testid={tid}>
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">{label}</div>
                <div className="text-xl font-black font-display tabular text-tac-text">{val}</div>
                <div className="text-[9px] text-tac-faint">unique visitors</div>
              </div>
            ))}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-2">
              Daily unique visitors
            </div>
            <div className="flex items-end gap-[2px] h-16" data-testid="analytics-daily-chart">
              {data.daily.map((d) => (
                <div
                  key={d.day}
                  title={`${d.day}: ${d.visitors}`}
                  className="flex-1 bg-tac-primary/70 hover:bg-tac-primary rounded-t-[1px] min-h-[2px]"
                  style={{ height: `${Math.max(3, (d.visitors / maxDay) * 100)}%` }}
                />
              ))}
            </div>
            <div className="flex justify-between text-[9px] font-mono-tac text-tac-faint mt-1">
              <span>{data.daily[0]?.day}</span>
              <span>{data.daily[data.daily.length - 1]?.day}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <BreakdownRows title="Device" icon={DeviceMobile} rows={data.device} total={data.unique_total} />
            <BreakdownRows title="System" icon={Desktop} rows={data.os} total={data.unique_total} />
            <BreakdownRows title="Browser" icon={Globe} rows={data.browser} total={data.unique_total} />
          </div>
          <div className="flex flex-wrap gap-6">
            <BreakdownRows title="Where From" icon={MapPin} rows={data.locations || []} total={data.unique_total} max={12} />
            <BreakdownRows title="Country" icon={Globe} rows={data.countries || []} total={data.unique_total} max={6} />
          </div>
          <div className="text-[9px] text-tac-faint">
            Anonymous — one ping per visitor per day. Location is approximate (city-level from IP). History kept 90 days.
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsCard;
